import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  UserPlus, CreditCard, BookOpen, ClipboardList,
  Users, DollarSign, Award, Briefcase, Library, Clock, FileText,
  MessageSquare, FolderOpen, Bell, Shield, Heart, Globe, Calendar,
  Mail, BarChart2, ArrowRight, Building, TrendingUp, Zap, CheckCircle
} from "lucide-react";

/* ─── DATA ─────────────────────────────────────────────────────────── */
const modules = [
  { icon: UserPlus, label: "Admission Forms", slug: "admissions" },
  { icon: CreditCard, label: "Fees Collection", slug: "fees" },
  { icon: ClipboardList, label: "Online Examinations", slug: "exams" },
  { icon: Award, label: "Results & Evaluation" },
  { icon: BookOpen, label: "LMS", slug: "lms" },
  { icon: Calendar, label: "Academic Planning" },
  { icon: Clock, label: "Student Attendance", slug: "attendance" },
  { icon: Users, label: "HR Management", slug: "hr" },
  { icon: DollarSign, label: "Payroll", slug: "payroll" },
  { icon: BarChart2, label: "OBE / NAAC" },
  { icon: FileText, label: "Certificates" },
  { icon: MessageSquare, label: "Feedback Forms" },
  { icon: FolderOpen, label: "Document Management" },
  { icon: Mail, label: "Inward / Outward" },
  { icon: Heart, label: "Mentor-Mentee" },
  { icon: Briefcase, label: "Placement Management", slug: "placement" },
  { icon: Globe, label: "Website Management" },
  { icon: Library, label: "Library" },
  { icon: Shield, label: "Visitor Management" },
  { icon: Bell, label: "Communications", slug: "whatsapp" },
  { icon: TrendingUp, label: "Reports & Analytics" },
];

const features = [
  { icon: Zap, n: "01", title: "All-in-One Platform", desc: "21 modules covering every aspect of institute management — from admissions to alumni, all interconnected." },
  { icon: Shield, n: "02", title: "Enterprise Security", desc: "Role-based access control, biometric integration, and full audit trails protect your institute's data." },
  { icon: BarChart2, n: "03", title: "NAAC Ready", desc: "Built-in OBE, IQAC, AQAR reporting simplifies accreditation workflows end-to-end." },
  { icon: Building, n: "04", title: "Multi-Campus", desc: "Manage multiple campuses and hundreds of departments from a single unified command center." },
  { icon: TrendingUp, n: "05", title: "Real-time Analytics", desc: "Live dashboards for fee collection rates, attendance trends, and placement outcomes." },
  { icon: Globe, n: "06", title: "Mobile First", desc: "Students and parents stay connected via the Acadence mobile app — notifications, results, fees." },
];

const testimonials = [
  { name: "Dr. Anjali Mehta", role: "Principal, VJTI Mumbai", text: "Acadence transformed how we operate. Fee collection efficiency went up 40% in the first semester." },
  { name: "Prof. Suresh Kumar", role: "Dean, NIT Surathkal", text: "The NAAC reporting module alone saved our IQAC team hundreds of hours of manual compilation." },
  { name: "Ms. Priya Rajan", role: "Registrar, Amity University", text: "Best ERP we've implemented. The student dashboard is intuitive — adoption was instant." },
];

const statsList = [
  { value: "500+", label: "Institutes" },
  { value: "2M+", label: "Students" },
  { value: "21", label: "Modules" },
  { value: "98%", label: "Satisfaction" },
];

/* ─── NAVBAR ────────────────────────────────────────────────────────── */
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  return (
    <motion.nav
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      data-testid="landing-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-[#0F1B3D]/95 backdrop-blur-md border-b border-[#1E2D55]" : "bg-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <svg viewBox="0 0 32 32" fill="none" className="w-8 h-8">
            <rect x="1" y="21" width="8" height="8" rx="2" fill="#2A3A66"/>
            <rect x="12" y="13" width="8" height="8" rx="2" fill="#4B7BF5" opacity="0.5"/>
            <rect x="22" y="5" width="8" height="8" rx="2" fill="#4B7BF5"/>
          </svg>
          <span className="font-['DM_Sans'] font-medium text-lg text-[#EEEADF] tracking-[0.08em]">acadence</span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <Link to="/features" className="font-['Syne'] text-xs font-medium uppercase tracking-widest text-[#EEEADF]/50 hover:text-[#EEEADF] transition-colors">
            Features
          </Link>
          <a href="#modules" className="font-['Syne'] text-xs font-medium uppercase tracking-widest text-[#EEEADF]/50 hover:text-[#EEEADF] transition-colors">
            Modules
          </a>
          <Link to="/docs" className="font-['Syne'] text-xs font-medium uppercase tracking-widest text-[#EEEADF]/50 hover:text-[#EEEADF] transition-colors">
            Docs
          </Link>
          <Link to="/contact" className="font-['Syne'] text-xs font-medium uppercase tracking-widest text-[#EEEADF]/50 hover:text-[#EEEADF] transition-colors">
            Contact
          </Link>
        </div>

        <div className="hidden md:flex items-center gap-4">
          <Link to="/login" data-testid="nav-login-btn" className="font-['Syne'] text-xs font-bold uppercase tracking-widest text-[#EEEADF]/60 hover:text-[#EEEADF] transition-colors px-4 py-2">
            Sign in
          </Link>
          <Link to="/contact" data-testid="nav-register-btn" className="font-['Syne'] text-xs font-bold uppercase tracking-widest bg-[#4B7BF5] text-white px-5 py-2.5 hover:bg-white hover:text-[#0F1B3D] transition-colors">
            Get a Demo
          </Link>
        </div>

        <button onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-btn" className="md:hidden text-[#EEEADF] p-2">
          <div className={`w-5 h-0.5 bg-current mb-1.5 transition-all ${menuOpen ? "rotate-45 translate-y-2" : ""}`} />
          <div className={`w-5 h-0.5 bg-current mb-1.5 transition-all ${menuOpen ? "opacity-0" : ""}`} />
          <div className={`w-5 h-0.5 bg-current transition-all ${menuOpen ? "-rotate-45 -translate-y-2" : ""}`} />
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-[#0F1B3D] border-t border-[#1E2D55] px-6 py-6 flex flex-col gap-5">
          <Link to="/features" onClick={() => setMenuOpen(false)} className="font-['Syne'] text-xs font-bold uppercase tracking-widest text-[#EEEADF]/50">Features</Link>
          <a href="#modules" onClick={() => setMenuOpen(false)} className="font-['Syne'] text-xs font-bold uppercase tracking-widest text-[#EEEADF]/50">Modules</a>
          <Link to="/docs" onClick={() => setMenuOpen(false)} className="font-['Syne'] text-xs font-bold uppercase tracking-widest text-[#EEEADF]/50">Docs</Link>
          <Link to="/contact" onClick={() => setMenuOpen(false)} className="font-['Syne'] text-xs font-bold uppercase tracking-widest text-[#EEEADF]/50">Contact</Link>
          <Link to="/login" className="font-['Syne'] text-xs font-bold uppercase tracking-widest text-[#EEEADF]/60">Sign in</Link>
          <Link to="/contact" className="font-['Syne'] text-xs font-bold uppercase tracking-widest bg-[#4B7BF5] text-white px-5 py-3 text-center">Get a Demo</Link>
        </div>
      )}
    </motion.nav>
  );
}

/* ─── HERO ──────────────────────────────────────────────────────────── */
function Hero() {
  return (
    <section className="relative min-h-screen bg-[#0F1B3D] noise-bg flex items-center pt-16 overflow-hidden" data-testid="hero-section">
      {/* Ambient glow */}
      <div className="absolute top-1/2 left-1/3 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-[#4B7BF5]/6 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#0A7AFF]/8 rounded-full blur-[100px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 py-24 w-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="grid lg:grid-cols-[1fr_400px] gap-16 items-center"
        >
          {/* Left — Typography hero */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.5 }}
              className="flex items-center gap-3 mb-10"
            >
              <span className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#4B7BF5]">01 — Ed-ERP Platform</span>
              <span className="h-px w-12 bg-[#4B7BF5]/30" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="font-['Instrument_Serif'] leading-[0.92] text-[#EEEADF] mb-8"
              style={{ fontSize: "clamp(3.2rem, 7vw, 6.5rem)" }}
            >
              Education,<br />
              <em className="italic text-[#4B7BF5]">Orchestrated.</em>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="text-[#EEEADF]/55 max-w-lg text-lg leading-relaxed mb-10 font-['DM_Sans']"
            >
              21 integrated modules. One platform. Complete institute management — from admissions to alumni placements.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="flex flex-wrap gap-4"
            >
              <Link to="/contact" data-testid="hero-cta-btn" className="inline-flex items-center gap-2 bg-[#4B7BF5] text-white font-['Syne'] font-bold text-xs uppercase tracking-widest px-8 py-4 hover:bg-white hover:text-[#0F1B3D] transition-colors active:scale-95">
                Request a Demo <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/login" data-testid="hero-demo-btn" className="inline-flex items-center gap-2 border border-[#EEEADF]/15 text-[#EEEADF]/70 font-['Syne'] font-bold text-xs uppercase tracking-widest px-8 py-4 hover:border-[#EEEADF]/40 hover:text-[#EEEADF] transition-colors">
                Sign In
              </Link>
            </motion.div>

            {/* Mini stats row */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.55, duration: 0.5 }}
              className="mt-14 pt-8 border-t border-[#1E2D55] flex flex-wrap gap-8"
            >
              {statsList.map((s, i) => (
                <div key={i}>
                  <p className="font-['JetBrains_Mono'] font-bold text-2xl text-[#EEEADF]">{s.value}</p>
                  <p className="font-['Syne'] text-[10px] uppercase tracking-[0.25em] text-[#EEEADF]/35 mt-0.5">{s.label}</p>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right — Image with editorial frame */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.25, duration: 0.6 }}
            className="hidden lg:block relative"
          >
            {/* Outer frame */}
            <div className="border border-[#1E2D55] bg-[#162044] p-4 relative">
              <img
                src="/screenshots/dashboard.png"
                alt="Acadence Dashboard — Admin Overview"
                className="w-full"
              />
              {/* Corner accent */}
              <div className="absolute top-0 right-0 w-8 h-8 bg-[#4B7BF5]" />
            </div>

            {/* Floating stat card — acid yellow */}
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-5 -left-8 bg-[#4B7BF5] px-5 py-3"
            >
              <p className="font-['JetBrains_Mono'] font-bold text-2xl text-white leading-none">2,847</p>
              <p className="font-['Syne'] font-bold text-[9px] uppercase tracking-[0.25em] text-white/60 mt-0.5">Students Active</p>
            </motion.div>

            {/* Secondary float */}
            <motion.div
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
              className="absolute -top-4 -right-6 bg-[#0F1B3D] border border-[#1E2D55] px-4 py-3"
            >
              <p className="font-['JetBrains_Mono'] font-bold text-xl text-[#4B7BF5] leading-none">98%</p>
              <p className="font-['Syne'] font-bold text-[9px] uppercase tracking-[0.25em] text-[#EEEADF]/40 mt-0.5">Uptime</p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── MARQUEE ───────────────────────────────────────────────────────── */
function Marquee() {
  const items = modules.map(m => m.label);
  const doubled = [...items, ...items];
  return (
    <div className="bg-[#0F1B3D] border-y border-[#1E2D55] py-4 overflow-hidden" data-testid="marquee-strip">
      <div className="marquee-track">
        {doubled.map((name, i) => (
          <span key={i} className="inline-flex items-center gap-4 px-8 font-['Syne'] text-[10px] font-bold uppercase tracking-[0.25em] text-[#EEEADF]/30 whitespace-nowrap">
            {name}
            <span className="text-[#4B7BF5] text-xs">+</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─── FEATURES ──────────────────────────────────────────────────────── */
function Features() {
  return (
    <section id="features" className="bg-[#F5F2EA] py-24" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-16"
        >
          <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#7A7468] mb-4">— Why Acadence</p>
          <h2 className="font-['Syne'] font-bold text-[#0F1B3D] leading-tight" style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}>
            Built for institutes<br />that move fast.
          </h2>
        </motion.div>

        {/* Editorial grid with 1px dividers */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#D8D4CB]">
          {features.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06, duration: 0.4 }}
              className="bg-[#F5F2EA] hover:bg-white transition-colors duration-200 p-8 group cursor-default"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="w-9 h-9 bg-[#0F1B3D] flex items-center justify-center">
                  <f.icon className="w-4 h-4 text-[#4B7BF5]" strokeWidth={1.5} />
                </div>
                <span className="font-['JetBrains_Mono'] text-xs text-[#D8D4CB] group-hover:text-[#4B7BF5] transition-colors">{f.n}</span>
              </div>
              <h3 className="font-['Syne'] font-bold text-lg text-[#0F1B3D] mb-3">{f.title}</h3>
              <p className="text-[#7A7468] text-sm leading-relaxed font-['DM_Sans']">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── MODULES ───────────────────────────────────────────────────────── */
function Modules() {
  return (
    <section id="modules" className="bg-[#0F1B3D] noise-bg py-24" data-testid="modules-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16"
        >
          <div>
            <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#4B7BF5] mb-4">— Complete Suite</p>
            <h2 className="font-['Syne'] font-bold text-[#EEEADF] leading-tight" style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}>
              21 modules.<br />One platform.
            </h2>
          </div>
          <p className="text-[#EEEADF]/30 font-['DM_Sans'] text-sm max-w-xs md:text-right leading-relaxed">
            Every process of your institute, digitized, connected, and reporting in real-time.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-[#1E2D55]">
          {modules.map((m, i) => {
            const inner = (
              <>
                <span className="font-['JetBrains_Mono'] text-[10px] text-[#4B7BF5]/40 w-7 flex-shrink-0 group-hover:text-[#4B7BF5] transition-colors">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <m.icon className="w-4 h-4 text-[#EEEADF]/25 group-hover:text-[#4B7BF5] transition-colors flex-shrink-0" strokeWidth={1.5} />
                <span className="font-['Syne'] font-medium text-sm text-[#EEEADF]/50 group-hover:text-[#EEEADF] transition-colors">
                  {m.label}
                </span>
                <ArrowRight className="w-3 h-3 text-[#4B7BF5]/0 group-hover:text-[#4B7BF5]/60 transition-all ml-auto" />
              </>
            );
            const cls = "flex items-center gap-4 py-4 px-3 border-b border-[#1E2D55] hover:bg-[#4B7BF5]/5 group cursor-pointer transition-colors";
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 6) * 0.04 }}
              >
                {m.slug ? (
                  <Link to={`/features/${m.slug}`} className={cls}>{inner}</Link>
                ) : (
                  <div className={cls}>{inner}</div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─── TESTIMONIALS ──────────────────────────────────────────────────── */
function Testimonials() {
  return (
    <section className="bg-[#F5F2EA] py-24" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#7A7468] mb-4">— Social Proof</p>
          <h2 className="font-['Syne'] font-bold text-4xl text-[#0F1B3D]">Loved by educators.</h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#D8D4CB]">
          {testimonials.map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-[#F5F2EA] p-8"
            >
              <div className="flex gap-0.5 mb-6">
                {Array.from({ length: 5 }).map((_, j) => (
                  <span key={j} className="text-[#4B7BF5] text-lg leading-none">★</span>
                ))}
              </div>
              <p className="font-['DM_Sans'] text-[#0F1B3D] text-base leading-relaxed mb-8">"{t.text}"</p>
              <div className="border-t border-[#D8D4CB] pt-5">
                <p className="font-['Syne'] font-bold text-sm text-[#0F1B3D]">{t.name}</p>
                <p className="font-['DM_Sans'] text-xs text-[#7A7468] mt-0.5">{t.role}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── CTA ───────────────────────────────────────────────────────────── */
function CTA() {
  return (
    <section className="bg-[#4B7BF5] py-24" data-testid="cta-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-12">
          <motion.h2
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="font-['Instrument_Serif'] text-white leading-tight max-w-2xl"
            style={{ fontSize: "clamp(2.5rem, 5vw, 5rem)" }}
          >
            Ready to transform your institute?
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex flex-col gap-4 flex-shrink-0"
          >
            <Link to="/contact" data-testid="cta-start-btn" className="bg-[#0F1B3D] text-white font-['Syne'] font-bold text-xs uppercase tracking-widest px-10 py-5 hover:bg-[#162044] transition-colors text-center">
              Request a Demo
            </Link>
            <div className="flex flex-col gap-2">
              {["Personalized walkthrough", "Go live in days", "Dedicated support"].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-white/70">
                  <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={2} />
                  <span className="font-['DM_Sans'] text-xs">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─── FOOTER ────────────────────────────────────────────────────────── */
function Footer() {
  return (
    <footer className="bg-[#0F1B3D] border-t border-[#1E2D55] py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-2.5">
          <svg viewBox="0 0 32 32" fill="none" className="w-7 h-7">
            <rect x="1" y="21" width="8" height="8" rx="2" fill="#2A3A66"/>
            <rect x="12" y="13" width="8" height="8" rx="2" fill="#4B7BF5" opacity="0.5"/>
            <rect x="22" y="5" width="8" height="8" rx="2" fill="#4B7BF5"/>
          </svg>
          <span className="font-['DM_Sans'] font-medium text-[#EEEADF] tracking-[0.08em]">acadence</span>
        </div>
        <p className="font-['DM_Sans'] text-[#EEEADF]/25 text-xs">© 2026 Acadence. All rights reserved.</p>
        <div className="flex gap-6">
          <Link to="/contact" className="font-['Syne'] text-[10px] font-bold uppercase tracking-widest text-[#EEEADF]/30 hover:text-[#EEEADF] transition-colors">Contact</Link>
          <a href="mailto:info@acadence.in" className="font-['Syne'] text-[10px] font-bold uppercase tracking-widest text-[#EEEADF]/30 hover:text-[#EEEADF] transition-colors">info@acadence.in</a>
        </div>
      </div>
    </footer>
  );
}

/* ─── LANDING ───────────────────────────────────────────────────────── */
export default function Landing() {
  useEffect(() => {
    document.title = "Acadence — Modern Education ERP | 21 Modules, One Platform";
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main>
        <Hero />
        <Marquee />
        <Features />
        <Modules />
        <Testimonials />
        <CTA />
      </main>
      <Footer />
    </div>
  );
}
